/**
 * AssignmentReportView — the presentational body of the Assignment Report
 * What's New experience. Pure: ready data + callbacks in, UI out, so the same
 * component backs the live student card AND the admin "preview in the context
 * of any user". No data fetching, no navigation, no app coupling.
 *
 * The report itself is rendered by the shared QuestionSessionReport component —
 * the very one the results page uses — inside a compact wrapper that scales the
 * type down for the modal. Same report, smaller; never a second report UI.
 *
 * See docs/FeatureDesigns/assignments-tests/whats-new-assignment-report.md.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { QuestionSessionReport } from '../question-session-report';
import type {
    QuestionSessionReportState,
    QuestionSessionReportHeadline,
} from '../question-session-report';
import './AssignmentReportView.css';

export interface AssignmentReportViewProps {
    status: 'loading' | 'error' | 'ready';
    /** Assignment (or test) title the report is about. */
    title?: string;
    /** ISO submitted-at of the attempt. */
    submittedAt?: string;
    /** practice-set ⇒ "assignment"; otherwise "test". Only affects wording. */
    isAssignment?: boolean;
    report?: QuestionSessionReportState | null;
    headline?: QuestionSessionReportHeadline;
    hasMarking?: boolean;
    /** Primary CTA — open the full report. */
    onOpenReport: () => void;
    /** Secondary defer control. Omit to hide (e.g. admin preview). */
    onLater?: () => void;
}

export function AssignmentReportView({
    status, isAssignment, report, onOpenReport, onLater,
}: AssignmentReportViewProps) {
    // The report can be taller than the modal. Rather than scroll it, crop to the
    // available height and fade the cut edge — the "View full report" CTA below
    // takes the student to the whole thing. `cropped` toggles the fade only when
    // content is actually clipped (short reports show in full, no fade). Hooks
    // run before the early returns to satisfy the rules-of-hooks.
    const reportRef = useRef<HTMLDivElement>(null);
    const [cropped, setCropped] = useState(false);
    useLayoutEffect(() => {
        const el = reportRef.current;
        if (!el) return;
        const measure = () => setCropped(el.scrollHeight > el.clientHeight + 4);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [report, status]);

    if (status === 'loading') {
        return <div className="asr"><p className="asr-loading">Fetching your latest report…</p></div>;
    }

    const noun = isAssignment === false ? 'test' : 'assignment';

    // The server gate means an eligible student always has a ready report; this
    // is the honest fallback for the cases it can't cover (a failed load, or a
    // report that stopped being ready between serve and render).
    if (status === 'error' || report?.status !== 'ready') {
        return (
            <div className="asr">
                <p className="asr-empty-title">No report to show just yet 📋</p>
                <p className="asr-empty-sub">
                    Submit an {noun} and we'll write up what went well and what to work on next.
                </p>
                {onLater && <div className="asr-actions"><button type="button" className="asr-later" onClick={onLater}>Close</button></div>}
            </div>
        );
    }

    return (
        <div className="asr">
            <p className="asr-lead">
                <span className="asr-lead-em">Your last {noun} report is in. ✨</span>{' '}
                See the actionable summary in your AI-generated report.
            </p>

            <div ref={reportRef} className={`asr-report${cropped ? ' asr-report--cropped' : ''}`}>
                <QuestionSessionReport report={report} />
            </div>

            <div className="asr-actions">
                <button type="button" className="asr-cta" onClick={onOpenReport}>View full report →</button>
                {onLater && <button type="button" className="asr-later" onClick={onLater}>Maybe later</button>}
            </div>
        </div>
    );
}
